import * as Yup from "yup";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import {DialogContent, DialogTitle} from "@mui/material";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import PartSupportRequestInformationRow from "../../app-specific/part-support/part-support-request-info/info-row";

const initialSchema = Yup.object().shape({
    note: Yup.string().trim().nullable(),
    internalNote: Yup.string().trim().nullable(),
});


const UpdatePartSupportRequestNoteModal = ({open, setOpen, request, onSuccess}) => {
    const [initialValues, setInitialValues] = useState({});
    const [loading, setLoading] = useState(false);
    const validate = makeValidate(initialSchema);
    const required = makeRequired(initialSchema);
    const isMounted = useIsMounted();

    const DataGridSaveConfig = useMemo(() => ({
        name: 'UpdatePartSupportRequestNoteDialogDataGrid',
        version: 1,
    }), [])

    /**
     * Listens for when the modal opens and closes, and if open, gets the list of
     */
    useEffect(() => {
        if (!open) {
            setLoading(false);
            return;
        }
        setInitialValues({
            note: request.note ?? '',
            internalNote: request.internalNote ?? '',
        });
    }, [open, request?.internalNote, request?.note])

    /**
     * Registers a new vendor into the system.
     * @param values
     */
    const onSubmit = useCallback(async (values) => {
        setLoading(true);
        const trimmedNote = values.note?.trim() ?? '';
        const trimmedInternalNote = values.internalNote?.trim() ?? '';
        const forApi = {
            note: trimmedNote.length ? trimmedNote : null,
            internalNote: trimmedInternalNote.length ? trimmedInternalNote : null,
            partSupportReqId: request.id,
        }
        const response = await Api.updatePartSupportRequestNote(forApi);
        if (!isMounted())
            return
        setLoading(false);
        if (response?.resultFlag) {
            onSuccess(request, forApi.note, forApi.internalNote);
            setOpen(false);
        }
    }, [isMounted, onSuccess, request, setOpen])

    const colClass = 'px-3 mb-4';
    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'md'}>
            <DialogTitle>
                <Row xs={12} className='no-gutters justify-content-center w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Update Part Support Request Note
                    </p>
                </Row>
            </DialogTitle>
            <DialogContent dividers className={'pb-0 px-0'}>
                {
                    request &&
                    <PartSupportRequestInformationRow
                        data={[request]}
                        saveConfig={DataGridSaveConfig}
                    />
                }
                <div className='my-3 w-100'/>
                <Form
                    id={'submission-form'}
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className={'no-gutters w-100 justify-content-start'}>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Note'}
                                            name={'note'}
                                            type={'text'}
                                            multiline
                                            minRows={15}
                                            required={required.note}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Internal Note'}
                                            name={'internalNote'}
                                            type={'text'}
                                            multiline
                                            minRows={15}
                                            required={required.internalNote}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-1 w-100'/>
                                <Row className={'justify-content-between align-items-start w-100 p-0'}>
                                    <Button
                                        disabled={!open}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={() => setOpen(false)}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!open || loading || (values?.note === initialValues?.note && values?.internalNote === initialValues?.internalNote)}
                                        variant={'dialog'}
                                        color={'primary'}
                                        form={'submission-form'}
                                        type={'submit'}
                                        borderRadius={'8px 0 8px 0'}
                                    >
                                        {!loading ? 'Save' : 'Processing...'}
                                    </Button>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

export default UpdatePartSupportRequestNoteModal;
